import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { CHANNELS } from "../../../shared/goals/channels";
import { scrollToTop, useNative } from "../../../shared/phone/media";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { Chevron, PnRow } from "../../../shared/phone/Row";
import { Icon } from "../../components/ui/Icon";
import { api, ApiError } from "../../lib/api";
import { goalsApi, type GoalView } from "../../lib/goalsApi";
import { libraryApi } from "../../lib/libraryApi";
import { productsApi, runsApi } from "../../lib/productsApi";
import type { SavePatch } from "./ChannelsStep";
import { ChannelsStep, channelTitle } from "./ChannelsStep";
import { FlowHead, type ProductRef } from "./FlowHead";
import { FullPlan } from "./FullPlan";
import { formOf, goalPatch, pageFromProfile } from "./goalForm";
import { GoalStep, type GoalForm, type GoalValues } from "./GoalStep";
import { FlowBar, Foot, ProductTile, Spin, StepHead, StepRail, stepLine } from "./Native";
import { AfterApprove, Expects, GoalHead, NotRightSheet, PlanScreen, planParts } from "./PlanReview";
import { PlanRun } from "./PlanRun";
import { PlaybookStep } from "./PlaybookStep";
import { callSection, PlanCalls, usePlanNotes, written } from "./PlanCalls";
import { Review } from "./Review";
import { META_STEPS } from "./steps";
import { ChangesSheet, ProductSheet } from "./Sheets";
import { fmt, isoDay, utcMs } from "./text";
import { useToast } from "./useToast";
import { GoalRoutes, type SetupProps } from "../cc/GoalRoutes";
import "../../styles/wizard.css";

const STEPS = ["Goal", "Channel", "Playbook", "Plan", "Review"] as const;
const TITLES: Record<number, [string, string?]> = {
  1: ["What do you want to achieve?", "Say what should happen, how many, and by when."],
  3: ["The playbook"],
  4: ["Writing the plan"],
  5: ["Review the plan"],
};
// Why: the channel is set up before the playbook, so the playbook is written for this goal on that channel.
const SETUP_STEP: Record<string, number> = { goal: 1, channel: 2, playbook: 3 };
const STEP_NAME = ["", "goal", "channel", "playbook"];
const BACK = { to: "/command", label: "Command Center" };
/** Back from a native step that cannot go back a step: Home, where the goal waits. */
const HOME = { to: "/command", label: "Home" };

/** The goal's short name, read from its sentence: at most 60 characters, cut at a word. */
const LABEL_MAX = 60;
function shortLabel(what: string): string {
  const s = what.trim();
  if (s.length <= LABEL_MAX) return s;
  const cut = s.lastIndexOf(" ", LABEL_MAX);
  return s.slice(0, cut > LABEL_MAX / 2 ? cut : LABEL_MAX).trim();
}

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/** A product to pick, with its one line about it. */
type Pick = ProductRef & { about: string | null };

function useProducts() {
  const [products, setProducts] = useState<Pick[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.products.list().then(
      (r) => setProducts(r.products.map((p) => ({ id: p.id, name: p.name, logoR2Key: p.logoR2Key, coldEmail: p.coldEmail, about: p.description }))),
      (e) => setError(message(e)),
    );
  }, []);
  return { products, error };
}

const blankForm = (): GoalForm => ({ sentence: "", budget: "", max: "", page: "" });

const timezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

// --- A new goal: /goals/new picks the product first, /products/:id/goals/new starts on the goal ------------

/**
 * A new goal runs one channel, picked on New goal: Meta ads unless the address asks for cold email
 * (`?channel=email`), which only a product with cold email offers.
 */
export function NewGoal() {
  const native = useNative();
  const { id: productId } = useParams();
  const [params] = useSearchParams();
  const channel = params.get("channel") === "email" ? "email" : "meta";
  const navigate = useNavigate();
  const { products: all, error } = useProducts();
  const { toast, say } = useToast();
  const [busy, setBusy] = useState(false);
  const products = all && channel === "email" ? all.filter((p) => p.coldEmail) : all;
  const product = products?.find((p) => p.id === productId) ?? null;
  const query = channel === "email" ? "?channel=email" : "";

  const create = async (v: GoalValues) => {
    if (!product) return;
    setBusy(true);
    try {
      const { id } = await goalsApi.create(product.id, {
        sentence: v.sentence,
        label: shortLabel(v.reading.what),
        target: v.reading.target,
        by: isoDay(v.reading.by),
        budgetCents: v.budgetCents,
        perResultMaxCents: v.perResultMaxCents,
        dailyMaxCents: null,
        timezone: timezone(),
        channels: { [channel]: true },
        ...(channel === "meta" && v.page ? { link: v.page } : {}),
      });
      navigate(`/goals/${id}?step=channel`, { replace: true });
    } catch (e) {
      say(message(e), false);
      setBusy(false);
    }
  };

  // Why: the page a Meta goal's ads send people to starts as the one the profile saw the site's buttons push.
  const [suggested, setSuggested] = useState<string | null>(null);
  useEffect(() => {
    if (!product || channel !== "meta") return;
    productsApi.profile(product.id).then(
      (r) => setSuggested(r.confirmed ? pageFromProfile(r.confirmed.profile) : null),
      () => setSuggested(null),
    );
  }, [product, channel]);

  const [title, sub] = TITLES[1];
  if (native) {
    const steps = channel === "meta" ? META_STEPS : STEPS;
    const pick = (id: string) => navigate(`/products/${id}/goals/new${query}`, { replace: true });
    return (
      <div className="pn-screen su-wiz">
        <FlowBar cancel sub={productId ? stepLine(1, steps.length) : "Pick a product"} />
        <StepRail steps={steps} step={productId ? 1 : 0} />
        {error ? (
          <p className="pn-empty">{error}</p>
        ) : !productId ? (
          <div className="pn-stack su-step">
            <StepHead title="Which product is it for?" sub="The goal, its plan and its ads belong to one product." />
            {products && (
              <div className="pn-grp">
                {products.map((p) => (
                  <button type="button" className="pn-row su-prow" key={p.id} onClick={() => pick(p.id)}>
                    <ProductTile product={p} size={40} />
                    <span className="m">
                      <span className="t">{p.name}</span>
                      {p.about ? <span className="s">{p.about}</span> : null}
                    </span>
                    <Chevron />
                  </button>
                ))}
                <PnRow to="/products/new" icon={<Icon name="plus" />} title="Add a product" className="su-acc" chevron={false} />
              </div>
            )}
          </div>
        ) : products && !product ? (
          <p className="pn-empty">{all?.some((p) => p.id === productId) ? "Cold email is not part of this product." : "That product is not in your account."}</p>
        ) : (
          <GoalStep
            initial={blankForm()}
            busy={busy || !product}
            onNext={(v) => void create(v)}
            page={channel === "meta" && product ? { productId: product.id, suggested } : undefined}
            next="Next: Channel"
            head={
              <>
                <StepHead title={title} sub={sub} />
                {product && (
                  <Link className="su-prodc" to={`/goals/new${query}`} replace>
                    <ProductTile product={product} size={22} />
                    <span>{product.name}</span>
                    <span className="pn-lnk">Change</span>
                  </Link>
                )}
              </>
            }
          />
        )}
        {toast}
      </div>
    );
  }
  return (
    <div className="wrap ga gw">
      <FlowHead back={BACK} crumb={`New goal · ${CHANNELS[channel].label}`} product={product} steps={channel === "meta" ? META_STEPS : STEPS} step={1} title={title} sub={sub} />
      {error ? (
        <p className="load-note">{error}</p>
      ) : products && productId && !product ? (
        <p className="load-note">{all?.some((p) => p.id === productId) ? "Cold email is not part of this product." : "That product is not in your account."}</p>
      ) : null}
      {(product || !productId) && (
        <GoalStep initial={blankForm()} busy={busy || !product} onNext={(v) => void create(v)} page={channel === "meta" && product ? { productId: product.id, suggested } : undefined} />
      )}
      {products && !productId && <ProductSheet products={products} onPick={(id) => navigate(`/products/${id}/goals/new${query}`, { replace: true })} onClose={() => navigate("/command")} />}
      {toast}
    </div>
  );
}

// --- /goals/:goalId and every page under it ---------------------------------------------------------------

export function GoalPage() {
  const { goalId = "", "*": rest = "" } = useParams();
  return <GoalRoutes key={goalId} goalId={goalId} rest={rest} setup={(p) => <GoalSetup goalId={goalId} {...p} />} />;
}

// --- A goal being set up, planned or reviewed; with `edit`, an approved goal planned again (Edit goal) ------

function GoalSetup({ goalId, rest, edit, onApproved }: { goalId: string } & SetupProps) {
  const native = useNative();
  const navigate = useNavigate();
  const base = edit?.base ?? `/goals/${goalId}`;
  // Why: one part of the plan opens at `plan/:part`; Change the goal's routes pass only "plan", so the part is read here.
  const partId = (useParams()["*"] ?? "").match(/(?:^|\/)plan\/([^/]+)$/)?.[1] ?? null;
  const planPage = rest === "plan" || partId !== null;
  const [notRight, setNotRight] = useState(false);
  const [params, setParams] = useSearchParams();
  const onPlaybookRun = useCallback((run: string | null) => setParams(run ? { step: "playbook", run } : { step: "playbook" }, { replace: true }), [setParams]);
  const { products } = useProducts();
  const { toast, say } = useToast();
  const [view, setView] = useState<GoalView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [how, setHow] = useState(false);
  const [changes, setChanges] = useState(false);
  const [playbookTitle, setPlaybookTitle] = useState<string | null>(null);
  const [profileConfirmed, setProfileConfirmed] = useState(false);

  const fail = useCallback((e: unknown) => say(message(e), false), [say]);
  const sayOk = useCallback((m: string, ok?: boolean) => say(m, ok !== false), [say]);

  const reload = useCallback(async () => {
    try {
      setView(await goalsApi.get(goalId));
      setLoadError(null);
    } catch (e) {
      setLoadError(message(e));
    }
  }, [goalId]);
  useEffect(() => {
    void reload();
  }, [reload]);

  const g = view?.goal;
  const config = g?.setup ?? view?.current?.config ?? null;
  const product = products?.find((p) => p.id === g?.productId) ?? null;
  const approvedStates = ["getting_ready", "live", "paused", "done"];
  const approved = !!g && approvedStates.includes(g.state);
  const shown = approved ? (view?.current ?? null) : (view?.proposal ?? null);
  const plan = shown?.plan ?? null;
  // Why: a Meta goal's plan step holds the finished plan too; the steps after it are Audience and Ads.
  const metaGoal = !!config?.channels.meta?.on;
  const step = !g ? 0 : g.state === "setting_up" ? (SETUP_STEP[params.get("step") ?? ""] ?? 2) : g.state === "planning" || metaGoal ? 4 : 5;
  const stage = !g ? "" : approved ? "approved" : g.state === "plan_ready" ? "review" : g.state === "planning" ? "planning" : "";

  const notes = usePlanNotes(goalId, shown && !approved ? shown.version : null);
  // Why: once the goal is past setting up, a setup step in the address (`?step=playbook&run=...`) no longer says where it is.
  const pastSetup = !!g && g.state !== "setting_up";
  useEffect(() => {
    if (pastSetup && (params.has("step") || params.has("run"))) setParams({}, { replace: true });
  }, [pastSetup, params, setParams]);

  const pinId = config?.library.find((p) => p.kind === "playbook")?.itemId ?? null;
  const productId = g?.productId ?? null;
  useEffect(() => {
    if (step < 4 || !pinId || !productId) return;
    libraryApi.item(pinId).then((r) => setPlaybookTitle(r.item.title), fail);
    productsApi.profile(productId).then((r) => setProfileConfirmed(r.confirmed !== null), fail);
  }, [step, pinId, productId, fail]);

  const save: SavePatch = useCallback(
    async (patch, ok) => {
      try {
        const r = await goalsApi.patchSetup(goalId, patch);
        setView((v) => (v ? { ...v, goal: { ...v.goal, setupRev: r.setupRev, setup: r.config, sentence: r.config.sentence } } : v));
        if (ok) say(ok);
        return true;
      } catch (e) {
        fail(e);
        return false;
      }
    },
    [goalId, say, fail],
  );

  const toStep = (i: number) => {
    // Why: on a phone the steps are one screen with its own back, so moving between them adds no history.
    setParams({ step: STEP_NAME[i] }, { replace: native });
    scrollToTop();
  };

  /** Ask for a plan, or a new one; resolves false when it could not be asked for. */
  const askPlan = async () => {
    setBusy(true);
    try {
      await goalsApi.plan(goalId);
      setHow(false);
      setChanges(false);
      await reload();
      scrollToTop();
      return true;
    } catch (e) {
      fail(e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveGoal = async (v: GoalValues) => {
    if (!config) return;
    setBusy(true);
    if (await save(goalPatch(v, config), "")) toStep(2);
    setBusy(false);
  };

  const approve = async () => {
    if (!view?.proposal) return;
    setBusy(true);
    try {
      const { version } = await goalsApi.approve(goalId, view.proposal.id);
      onApproved(version);
    } catch (e) {
      fail(e);
      // Why: the approval can land before a later step of the request fails; the page shows what is true.
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const comment = async (section: string, body: string) => {
    if (!shown) return false;
    try {
      await goalsApi.comment(goalId, shown.version, { section, body });
      say("Comment added. Send your comments with “Ask for changes”.");
      await reload();
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  };

  /** The owner's "Not right" words become comments on the plan they were written against; false when one could not be saved. */
  const sendNotes = async () => {
    if (!shown) return true;
    try {
      for (const [call, body] of Object.entries(notes.notes)) {
        if (body.trim()) await goalsApi.comment(goalId, shown.version, { section: callSection(call), body: body.trim() });
        notes.set(call, null);
      }
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  };

  const sendChanges = async (ask: string | null) => {
    if (ask && !(await comment("The whole plan", ask))) return;
    if (!(await sendNotes())) return;
    await askPlan();
    if (planPage) navigate(base);
  };

  /** "Not right" on a phone: what should change goes to the agent with any comments waiting, and it plans again. */
  const sendNotRight = async (ask: string | null) => {
    if (!shown) return;
    if (ask) {
      try {
        await goalsApi.comment(goalId, shown.version, { section: "The whole plan", body: ask });
      } catch (e) {
        fail(e);
        return;
      }
    }
    if (!(await sendNotes()) || !(await askPlan())) return;
    setNotRight(false);
    say(`Sent. The agent writes version ${shown.version + 1}.`);
    if (planPage) navigate(base);
  };

  const unsent = useMemo(() => (view?.comments ?? []).filter((c) => !c.sent_at), [view]);
  // Why: the owner's "Not right" words and any comments waiting change the plan only once it is planned again.
  const mine = written(notes.notes).length;
  const waiting = mine + unsent.length;
  const planAgain = (
    <button type="button" className="btn" disabled={busy} onClick={() => void sendChanges(null)}>
      Plan again with your changes
    </button>
  );

  useEffect(() => {
    if (how) document.getElementById("pnow")?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
  }, [how]);

  // Why: "Written by the agent in N seconds" counts from when the agent started, which only the run knows.
  const planRunId = view?.planRun?.id ?? null;
  const [runTimes, setRunTimes] = useState<{ id: string; secs: number | null } | null>(null);
  useEffect(() => {
    if (rest !== "plan" || !planRunId) return;
    runsApi.get(planRunId).then(({ run }) => {
      const secs = run.started_at && run.ended_at ? Math.round((utcMs(run.ended_at) - utcMs(run.started_at)) / 1000) : null;
      setRunTimes({ id: run.id, secs });
    }, fail);
  }, [rest, planRunId, fail]);

  if (native) {
    if (!view || !g || !config)
      return (
        <div className="pn-screen">
          <PhoneNav title={edit ? "Change the goal" : "New goal"} back={edit?.back ?? HOME} />
          <p className="pn-empty">{loadError ?? "Loading the goal…"}</p>
        </div>
      );
    const planRun = view.planRun;
    const stale = !approved && view.proposal ? !view.proposal.madeFromCurrentSetup : false;
    const mark = product ? <ProductTile product={product} size={18} /> : null;
    const run = planRun ? (
      <PlanRun
        key={planRun.id}
        runId={planRun.id}
        show
        plan={g.state === "planning" ? null : plan}
        approved={approved}
        playbookTitle={playbookTitle}
        playbookItem={pinId}
        skillNames={config.library.filter((p) => p.kind === "skill").map((p) => p.name)}
        productName={product?.name ?? ""}
        profileConfirmed={profileConfirmed}
        onEnded={() => void reload()}
        onAgain={() => void askPlan()}
        againBusy={busy}
      />
    ) : null;
    const approveLabel = !edit && metaGoal ? "Next: Audience" : "Approve the plan";
    const foot = approved ? null : mine > 0 ? (
      <Foot>
        <button type="button" className="pn-btn pri wide" disabled={busy} onClick={() => void sendChanges(null)}>
          {busy ? <Spin /> : null}
          Plan again with your changes
        </button>
      </Foot>
    ) : (
      <Foot why={stale ? "Plan again to include your new settings." : null}>
        <div className="pn-btns">
          <button type="button" className="pn-btn" disabled={busy} onClick={() => setNotRight(true)}>
            Not right
          </button>
          <button type="button" className="pn-btn pri" disabled={stale || busy} onClick={() => void approve()}>
            {approveLabel}
            {approveLabel.startsWith("Next") ? <Icon name="right" /> : null}
          </button>
        </div>
      </Foot>
    );
    const sheets = (
      <>
        <NotRightSheet key={shown?.version ?? 0} open={notRight} onClose={() => setNotRight(false)} unsent={unsent} busy={busy} onSend={(a) => void sendNotRight(a)} />
        {toast}
      </>
    );
    const staleCard = stale ? (
      <div className="pn-card su-state">
        <p>Your settings changed after this plan was made. Plan again to include them.</p>
        <button type="button" className="pn-btn sm soft" disabled={busy} onClick={() => void askPlan()}>
          Plan again
        </button>
      </div>
    ) : null;
    const readRow = (title: string) =>
      plan && shown ? (
        <div className="pn-grp">
          <PnRow to={`${base}/plan`} icon={<Icon name="doc" />} iconTone="violet" title={title} sub={`Version ${shown.version}, ${planParts(plan).length} parts`} />
        </div>
      ) : null;

    // --- The plan, one part at a time ---
    if (planPage) {
      if (!plan || !shown) return <Navigate to={base} replace />;
      return (
        <>
          <PlanScreen plan={plan} version={shown.version} approved={approved} sentence={g.sentence} mark={mark} base={base} partId={partId} back={{ to: base, label: edit ? "Goal" : "Review" }} foot={foot} />
          {sheets}
        </>
      );
    }

    // --- Planned again from Change the goal: the goal waits on its plan, as its page shows it ---
    if (edit) {
      const planning = g.state === "planning";
      return (
        <div className="pn-screen">
          <PhoneNav title="" back={edit.back} />
          <div className="pn-stack">
            <GoalHead
              mark={product ? <ProductTile product={product} size={20} /> : null}
              product={product?.name ?? ""}
              sentence={g.sentence}
              state={planning ? "Writing the plan" : "Plan to review"}
              budget={`$${fmt(config.money.budgetCents / 100)} budget`}
            />
            {planning ? (
              <section>
                <div className="pn-cap">Writing the plan</div>
                {run}
                <p className="pn-note">The plan appears here as soon as the agent saves it. Nothing runs until you approve it.</p>
              </section>
            ) : (
              <>
                <div className="pn-card su-next">
                  <span className="pn-ico violet">
                    <Icon name="doc" />
                  </span>
                  <div>
                    <h3>The plan is ready for you</h3>
                    <p>Read it, then approve it or say what is not right. Nothing runs until you approve.</p>
                  </div>
                </div>
                {staleCard}
                {readRow("Read the plan")}
                {plan && <PlanCalls plan={plan} locked={approved} notes={notes.notes} onSet={notes.set} />}
                {run}
              </>
            )}
          </div>
          {!planning && foot}
          {sheets}
        </div>
      );
    }

    // --- A new goal's steps ---
    const steps = metaGoal ? META_STEPS : STEPS;
    const back =
      step === 2 ? { to: `${base}?step=goal`, label: "Goal", onBack: () => toStep(1) } : step === 3 ? { to: `${base}?step=channel`, label: "Channel", onBack: () => toStep(2) } : HOME;
    let body: ReactNode;
    if (step === 1)
      body = (
        <GoalStep
          initial={formOf(config)}
          busy={busy}
          onNext={(v) => void saveGoal(v)}
          page={config.channels.meta?.on && productId ? { productId, suggested: null } : undefined}
          next="Next: Channel"
          head={<StepHead title="What do you want to achieve?" sub="Say what should happen, how many, and by when." />}
        />
      );
    else if (step === 2)
      body = (
        <ChannelsStep
          config={config}
          save={save}
          say={sayOk}
          onBack={() => toStep(1)}
          onNext={() => toStep(3)}
          busy={busy}
          next="Next: Playbook"
          head={<StepHead title={channelTitle(config)} sub="What the agent does on its own, and what it asks you first." />}
        />
      );
    else if (step === 3) {
      const head = <StepHead title="The playbook" sub={product ? `How ${product.name} markets. The plan follows it.` : undefined} />;
      body = product ? (
        <PlaybookStep
          goalId={goalId}
          product={product}
          config={config}
          runId={params.get("run")}
          onRun={onPlaybookRun}
          save={save}
          say={sayOk}
          onBack={() => toStep(2)}
          onNext={() => void askPlan()}
          next="Draft the plan"
          busy={busy}
          head={head}
        />
      ) : (
        <div className="pn-stack su-step">{head}</div>
      );
    } else if (g.state === "planning" || !plan || !shown) {
      const stopped = planRun?.state === "failed" || planRun?.state === "cancelled";
      body = (
        <>
          <div className="pn-stack su-step">
            <StepHead title="Writing the plan" sub="The plan appears here as soon as the agent saves it. Nothing runs until you approve it." />
            {run}
          </div>
          {!stopped && (
            <Foot>
              <button type="button" className="pn-btn pri wide" disabled>
                <Spin />
                Writing the plan
              </button>
            </Foot>
          )}
        </>
      );
    } else
      body = (
        <>
          <div className="pn-stack su-step">
            <StepHead title="Review the plan" sub={metaGoal ? "Everything stands until you say it is not right." : "Nothing runs until you approve."} />
            {staleCard}
            {readRow("The full plan")}
            <PlanCalls plan={plan} locked={approved} notes={notes.notes} onSet={notes.set} />
            <Expects plan={plan} target={config.result.target} />
            {!metaGoal && <AfterApprove plan={plan} />}
            {run}
          </div>
          {foot}
        </>
      );
    return (
      <div className="pn-screen su-wiz">
        <FlowBar sub={stepLine(step, steps.length)} back={back} cancel={step === 1} />
        <StepRail steps={steps} step={step} canGo={(i) => g.state === "setting_up" && i <= 3} onGo={toStep} />
        {body}
        {sheets}
      </div>
    );
  }

  if (loadError && !view) return <div className="wrap ga gw"><p className="load-note">{loadError}</p></div>;
  if (!view || !g || !config) return <div className="wrap ga gw"><p className="load-note">Loading the goal…</p></div>;

  const planRun = view.planRun;
  const runSecs = runTimes && runTimes.id === planRun?.id ? runTimes.secs : null;
  const skillNames = config.library.filter((p) => p.kind === "skill").map((p) => p.name);

  // --- The full plan, its own page ---
  if (rest === "plan") {
    if (!plan || !shown) return <Navigate to={base} replace />;
    return (
      <div className="wrap ga gw" data-stage={stage}>
        <FlowHead back={{ to: base, label: metaGoal ? "Plan" : "Review" }} crumb="Plan" title="The full plan" goalRef={g.sentence} />
        <FullPlan
          plan={plan}
          version={shown.version}
          approved={approved}
          comments={view.comments}
          small={runSecs !== null ? `Written by the agent in ${runSecs} seconds` : ""}
          onComment={comment}
          onChanges={() => setChanges(true)}
          onDone={() => navigate(base)}
        />
        <ChangesSheet open={changes} onClose={() => setChanges(false)} unsent={unsent} busy={busy} onSend={(a) => void sendChanges(a)} />
        {toast}
      </div>
    );
  }

  const [title, sub] = step === 2 ? [channelTitle(config)] : (TITLES[step] ?? TITLES[5]);
  const stale = !approved && view.proposal ? !view.proposal.madeFromCurrentSetup : false;

  return (
    <div className="wrap ga gw" data-stage={stage}>
      <FlowHead
        back={edit?.back ?? BACK}
        crumb={edit ? "Edit goal" : "New goal"}
        product={product}
        steps={metaGoal ? META_STEPS : STEPS}
        step={step}
        canGo={(i) => g.state === "setting_up" && i <= 3}
        onGo={toStep}
        title={title}
        sub={sub}
        goalRef={g.sentence}
      />
      {step === 1 && <GoalStep initial={formOf(config)} busy={busy} onNext={(v) => void saveGoal(v)} page={config.channels.meta?.on && productId ? { productId, suggested: null } : undefined} />}
      {step === 2 && <ChannelsStep config={config} save={save} say={sayOk} onBack={() => toStep(1)} onNext={() => toStep(3)} busy={busy} next="Next: the playbook" />}
      {step === 3 && product && (
        <PlaybookStep
          goalId={goalId}
          product={product}
          config={config}
          runId={params.get("run")}
          onRun={onPlaybookRun}
          save={save}
          say={sayOk}
          onBack={() => toStep(2)}
          onNext={() => void askPlan()}
          next="Draft the plan"
          busy={busy}
        />
      )}
      {step >= 4 && planRun && (
        <PlanRun
          key={planRun.id}
          runId={planRun.id}
          show={step === 4 || how}
          plan={g.state === "planning" ? null : plan}
          approved={approved}
          playbookTitle={playbookTitle}
          playbookItem={pinId}
          skillNames={skillNames}
          productName={product?.name ?? ""}
          profileConfirmed={profileConfirmed}
          onEnded={() => void reload()}
          onAgain={() => void askPlan()}
          againBusy={busy}
        />
      )}
      {metaGoal && g.state === "plan_ready" && plan && shown && (
        <>
          {stale && (
            <section className="card psec" style={{ borderColor: "var(--warn-line)", marginTop: 16 }}>
              <div className="clear">
                <Icon name="info" />
                <span>Your settings changed after this plan was made. Plan again to include them.</span>
                <button type="button" className="btn sm" disabled={busy} onClick={() => void askPlan()}>
                  Plan again
                </button>
              </div>
            </section>
          )}
          <div style={{ marginTop: 16 }}>
            <PlanCalls plan={plan} locked={false} notes={notes.notes} onSet={notes.set} />
          </div>
          <div className="wz-nav">
            <span className="sp" />
            <Link className="btn ghost" to={`${base}/plan`}>
              The full plan
            </Link>
            {waiting > 0 && planAgain}
            {!mine && (
              <button type="button" className={`btn${waiting ? " ghost" : ""}`} disabled={stale || busy} onClick={() => void approve()}>
                {edit ? (
                  <>
                    <Icon name="check" />
                    Approve the plan
                  </>
                ) : (
                  <>
                    Next: audience
                    <Icon name="right" />
                  </>
                )}
              </button>
            )}
          </div>
        </>
      )}
      {step === 4 && g.state === "planning" && (
        <div className="card clear" style={{ marginTop: 16 }}>
          <Icon name="clock" />
          <span>The plan appears here as soon as the agent saves it. Nothing runs until you approve it.</span>
        </div>
      )}
      {step === 5 && !metaGoal && plan && shown && (
        <>
          <Review
            planHref={`${base}/plan`}
            plan={plan}
            config={approved ? (view.current?.config ?? config) : config}
            version={shown.version}
            approved={approved}
            calls={<PlanCalls plan={plan} locked={approved} notes={notes.notes} onSet={notes.set} />}
            stale={stale}
            onPlanAgain={() => void askPlan()}
            planBusy={busy}
            onHow={() => setHow((h) => !h)}
          />
          {!approved && (
            <div className="pfoot">
              <div className="pf-t">
                <b>Nothing runs until you approve.</b>
                {unsent.length ? ` ${unsent.length} comment${unsent.length > 1 ? "s" : ""} ready to send.` : ""}
              </div>
              <button type="button" className="btn ghost" onClick={() => setChanges(true)}>
                Ask for changes
              </button>
              {waiting > 0 && planAgain}
              {!mine && (
                <button type="button" className={`btn${waiting ? " ghost" : ""}`} disabled={stale || busy} onClick={() => void approve()}>
                  <Icon name="check" />
                  Approve the plan
                </button>
              )}
            </div>
          )}
        </>
      )}
      <ChangesSheet open={changes} onClose={() => setChanges(false)} unsent={unsent} busy={busy} onSend={(a) => void sendChanges(a)} />
      {toast}
    </div>
  );
}
